/**
 * Service for managing groups
 * Communicates with the ColdFusion REST API
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/groups';

/**
 * Get group name in specified language with fallback
 * @param {Object} group - Group object with translation fields
 * @param {string} lang - Language code (fr, en, nl, de, es, it, pt, el)
 * @returns {string} Group name in specified language or fallback
 */
export const getGroupName = (group, lang = 'fr') => {
    if (!group) return '';
    const langKey = `name_${lang}`;
    return group[langKey] || group.name_fr || group.name_en || '';
};

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();
    console.log('Groups API Response status:', response.status);

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || result.MESSAGE || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Get all groups for a user
 * @param {number} createdById - User's ID
 * @returns {Promise<Array>} Array of groups
 */
export const getUserGroups = async (createdById) => {
    try {
        const params = new URLSearchParams();
        if (createdById) {
            params.append('createdById', createdById.toString());
        }

        console.log('Fetching user groups from API...');
        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Groups API raw result:', result);
        const data = result.data || result.DATA || [];
        console.log('Groups data array:', data);

        // Normalize data for frontend compatibility
        // API returns lowercase keys and translation fields (name_fr, name_en, etc.)
        return data.map(group => ({
            id: group.id || group.ID,
            groupUuid: group.groupuuid || group.groupUuid || group.GROUPUUID,
            // Translation fields
            name_fr: group.name_fr || group.NAME_FR,
            name_en: group.name_en || group.NAME_EN,
            name_nl: group.name_nl || group.NAME_NL,
            name_de: group.name_de || group.NAME_DE,
            name_es: group.name_es || group.NAME_ES,
            name_it: group.name_it || group.NAME_IT,
            name_pt: group.name_pt || group.NAME_PT,
            name_el: group.name_el || group.NAME_EL,
            sourceLang: group.sourcelang || group.sourceLang || group.SOURCELANG || 'fr',
            groupNameId: group.groupnameid || group.groupNameId || group.GROUPNAMEID,
            createdById: group.createdbyid || group.createdById || group.CREATEDBYID,
            creatorName: group.creatorname || group.creatorName || group.CREATORNAME,
            creatorEmail: group.creatoremail || group.creatorEmail || group.CREATOREMAIL,
            memberCount: group.membercount || group.memberCount || group.MEMBERCOUNT || 0,
            status: group.status || group.STATUS,
            createdAt: group.createdat || group.createdAt || group.CREATEDAT,
            updatedAt: group.updatedat || group.updatedAt || group.UPDATEDAT,
            members: [],
            invitations: []
        }));
    } catch (error) {
        console.error('Error getting groups:', error);
        return [];
    }
};

/**
 * Get a single group by UUID with members
 * @param {string} groupUuid - Group UUID
 * @returns {Promise<Object|null>} Group object with members or null
 */
export const getGroupById = async (groupUuid) => {
    try {
        console.log('Fetching group by UUID:', groupUuid);
        const response = await fetch(`${API_BASE_URL}/${groupUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const group = result.data || result.DATA;
        const members = result.members || result.MEMBERS || [];

        if (!group) return null;

        return {
            id: group.id || group.ID,
            groupUuid: group.groupUuid || group.GROUPUUID,
            // Translation fields
            name_fr: group.name_fr || group.NAME_FR,
            name_en: group.name_en || group.NAME_EN,
            name_nl: group.name_nl || group.NAME_NL,
            name_de: group.name_de || group.NAME_DE,
            name_es: group.name_es || group.NAME_ES,
            name_it: group.name_it || group.NAME_IT,
            name_pt: group.name_pt || group.NAME_PT,
            name_el: group.name_el || group.NAME_EL,
            sourceLang: group.sourceLang || group.SOURCELANG || 'fr',
            groupNameId: group.groupNameId || group.GROUPNAMEID,
            createdById: group.createdById || group.CREATEDBYID,
            creatorName: group.creatorName || group.CREATORNAME,
            creatorEmail: group.creatorEmail || group.CREATOREMAIL,
            createdAt: group.createdAt || group.CREATEDAT,
            updatedAt: group.updatedAt || group.UPDATEDAT,
            members: members.map(m => ({
                contactId: m.contactid || m.contactId || m.CONTACTID,
                peopleId: m.peopleid || m.peopleId || m.PEOPLEID,
                peopleUuid: m.peopleuuid || m.peopleUuid || m.PEOPLEUUID,
                firstName: m.firstname || m.firstName || m.FIRSTNAME,
                lastName: m.lastname || m.lastName || m.LASTNAME,
                email: m.email || m.EMAIL,
                company: m.company || m.COMPANY,
                position: m.position || m.POSITION,
                invitedEmail: m.invitedemail || m.invitedEmail || m.INVITEDEMAIL,
                status: m.status || m.STATUS,
                addedAt: m.addedat || m.addedAt || m.ADDEDAT,
                type: (m.peopleid || m.peopleId || m.PEOPLEID) ? 'cabaza' : 'invited'
            })),
            invitations: members
                .filter(m => !m.peopleid && !m.peopleId && !m.PEOPLEID)
                .map(m => ({
                    email: m.invitedemail || m.invitedEmail || m.INVITEDEMAIL || m.email || m.EMAIL,
                    status: m.status || m.STATUS,
                    sentAt: m.addedat || m.addedAt || m.ADDEDAT
                }))
        };
    } catch (error) {
        console.error('Error getting group:', error);
        return null;
    }
};

/**
 * Create a new group
 * @param {Object} groupData - Group data
 * @param {number} groupData.groupNameId - Reference to groupnames table (required)
 * @param {number} groupData.createdById - Creator's ID
 * @returns {Promise<Object>} Created group
 */
export const createGroup = async (groupData) => {
    try {
        console.log('Creating group:', groupData);

        const payload = {
            groupNameId: groupData.groupNameId,
            createdById: groupData.createdById
        };

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const result = await handleResponse(response);
        const group = result.data || result.DATA;

        console.log('Group created:', result);

        return {
            id: group.id || group.ID,
            groupUuid: group.groupuuid || group.groupUuid || group.GROUPUUID,
            // Translation fields (from JOIN with groupnames)
            name_fr: group.name_fr || group.NAME_FR,
            name_en: group.name_en || group.NAME_EN,
            name_nl: group.name_nl || group.NAME_NL,
            name_de: group.name_de || group.NAME_DE,
            name_es: group.name_es || group.NAME_ES,
            name_it: group.name_it || group.NAME_IT,
            name_pt: group.name_pt || group.NAME_PT,
            name_el: group.name_el || group.NAME_EL,
            sourceLang: group.sourcelang || group.sourceLang || group.SOURCELANG || 'fr',
            groupNameId: group.groupnameid || group.groupNameId || group.GROUPNAMEID,
            createdById: group.createdbyid || group.createdById || group.CREATEDBYID,
            creatorName: group.creatorname || group.creatorName || group.CREATORNAME,
            createdAt: group.createdat || group.createdAt || group.CREATEDAT,
            memberCount: 0,
            members: [],
            invitations: []
        };
    } catch (error) {
        console.error('Error creating group:', error);
        throw error;
    }
};

/**
 * Update a group
 * @param {string} groupUuid - Group UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated group
 */
export const updateGroup = async (groupUuid, updates) => {
    try {
        console.log('Updating group:', groupUuid, updates);

        const response = await fetch(`${API_BASE_URL}/${groupUuid}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                groupName: updates.name,
                status: updates.status
            })
        });

        const result = await handleResponse(response);
        console.log('Group updated:', result);

        return result.data || result.DATA;
    } catch (error) {
        console.error('Error updating group:', error);
        throw error;
    }
};

/**
 * Delete a group
 * @param {string} groupUuid - Group UUID
 * @returns {Promise<boolean>} Success status
 */
export const deleteGroup = async (groupUuid) => {
    try {
        console.log('Deleting group:', groupUuid);

        const response = await fetch(`${API_BASE_URL}/${groupUuid}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        await handleResponse(response);
        console.log('Group deleted successfully');
        return true;
    } catch (error) {
        console.error('Error deleting group:', error);
        return false;
    }
};

/**
 * Add a Cabaza member to a group
 * @param {string} groupUuid - Group UUID
 * @param {Object} member - Member data { id (contactId), firstName, lastName, email }
 * @returns {Promise<Object>} Result
 */
export const addMemberToGroup = async (groupUuid, member) => {
    try {
        console.log('Adding member to group:', groupUuid, member);

        const response = await fetch(`${API_BASE_URL}/${groupUuid}/members`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                contactId: member.id  // member.id is the contact ID from contacts table
            })
        });

        const result = await handleResponse(response);
        console.log('Member added:', result);
        return result;
    } catch (error) {
        console.error('Error adding member to group:', error);
        throw error;
    }
};

/**
 * Remove a member from a group
 * @param {string} groupUuid - Group UUID
 * @param {number} contactId - Contact ID (from contacts table)
 * @returns {Promise<Object>} Result
 */
export const removeMemberFromGroup = async (groupUuid, contactId) => {
    try {
        console.log('Removing member from group:', groupUuid, contactId);

        const response = await fetch(`${API_BASE_URL}/${groupUuid}/members/${contactId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Member removed:', result);
        return result;
    } catch (error) {
        console.error('Error removing member from group:', error);
        throw error;
    }
};

/**
 * Invite someone by email to a group
 * @param {string} groupUuid - Group UUID
 * @param {string} email - Email to invite
 * @returns {Promise<Object>} Result
 */
export const inviteToGroup = async (groupUuid, email) => {
    try {
        console.log('Inviting to group:', groupUuid, email);

        const response = await fetch(`${API_BASE_URL}/${groupUuid}/members`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                invitedEmail: email
            })
        });

        const result = await handleResponse(response);
        console.log('Invitation sent:', result);
        return result;
    } catch (error) {
        console.error('Error inviting to group:', error);
        throw error;
    }
};

/**
 * Remove an invitation from a group
 * @param {string} groupUuid - Group UUID
 * @param {number} contactId - Contact ID of the invitation
 * @returns {Promise<Object>} Result
 */
export const removeInvitation = async (groupUuid, contactId) => {
    return removeMemberFromGroup(groupUuid, contactId);
};
